import {
  alpha,
  Card,
  CardContent,
  Grid,
  Radio,
  Typography,
} from '@mui/material';
import { getMeasurementLabel } from '@farmshare/utils';

export interface SpecificationOption {
  value: string;
  label: string;
  description?: string;
  isDefault?: boolean;
}

export interface SpecificationOptionCardProps {
  option: SpecificationOption;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  measurementUnit?: string;
  name?: string;
  imageUrl?: string;
}

export function SpecificationOptionCard({
  option,
  checked,
  disabled,
  onChange,
  measurementUnit,
  name,
}: SpecificationOptionCardProps) {
  const handleCardClick = () => {
    if (disabled || !onChange) return;

    const syntheticEvent = {
      target: {
        name: name || '',
        value: option.value,
        checked: !checked,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  return (
    <Card
      data-testid="specification-option-card"
      onClick={handleCardClick}
      sx={{
        borderRadius: '9px',
        my: 2,
        width: '100%',
        border: '2px solid', // Always 2px border to prevent layout shift
        borderColor: checked ? 'primary.main' : 'divider', // Divider color when not selected

        // selected state
        ...(checked
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
              // improve readability on colored background
              '& .speccard-title': {
                color: 'primary.contrastText',
              },
            }
          : {}),

        // hover (only when not disabled)
        ...(disabled
          ? { opacity: 0.7, cursor: 'not-allowed' }
          : {
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: (theme) =>
                  checked
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
              },
            }),

        // Ensure all child elements use pointer cursor when not disabled
        ...(!disabled && {
          '& *': {
            cursor: 'pointer !important',
          },
        }),
      }}
      variant="outlined"
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              name={name}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              size="medium"
            />
          </Grid>
          <Grid sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontSize: '1.125rem',
                fontWeight: '500',
                lineHeight: '1.25rem',
                mb: option.description ? 0.5 : 0,
              }}
            >
              {`${option.label}${measurementUnit ? ` ${getMeasurementLabel(measurementUnit)}` : ''}`}
            </Typography>
            {option.description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                {option.description}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
